// IssuesListPage.jsx
import React, {
  useState,
  useEffect,
  useContext,
  useCallback,
  useRef,
} from "react";
import styled from "styled-components";
import {
  X,
  Search,
  Filter,
  ChevronLeft,
  ChevronRight,
  Plus,
  ThumbsUp,
  Lightbulb,
  MessageSquare,
  MoreVertical,
  Edit,
  Trash2,
  CheckCircle2,
  User,
  Loader2,
  Cpu,
} from "lucide-react";
import { PlusCircle } from "lucide-react";
import { Range } from "react-range";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { oneDark } from "react-syntax-highlighter/dist/esm/styles/prism";
import {
  collection,
  query,
  orderBy,
  limit,
  getDocs,
  startAfter,
  serverTimestamp,
  addDoc,
  deleteDoc,
  where,
  doc,
  updateDoc,
  increment,
  onSnapshot,
  arrayUnion,
  setDoc,
  getDoc,
} from "firebase/firestore";
import { db } from "../firebase/firebaseConfig";
import { useNavigate } from "react-router-dom";
import { UserContext } from "../context/UserContext";
import AuthModal from "../components/modal/AuthModal";
import useSEO from "../hooks/useSeo";

/* --------------------------------------------------------------------------
   Styled Components (polished, purple theme, fixed card size + responsive)
   -------------------------------------------------------------------------- */

const Container = styled.div`
  display: flex;
  flex-direction: row;
  background: radial-gradient(circle at top left, #0b0617, #000);
  color: #fff;
  min-height: 100vh;
  padding: 24px;
  gap: 20px;

  @media (max-width: 900px) {
    flex-direction: column;
    padding: 12px;
  }
`;

const Sidebar = styled.aside`
  width: 280px;
  padding: 20px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(155, 92, 255, 0.12);
  position: sticky;
  top: 20px;
  height: fit-content;
  flex-shrink: 0;

  h3 {
    font-size: 1.05rem;
    color: #c59fff;
    margin-bottom: 12px;
  }

  @media (max-width: 900px) {
    display: none;
  }
`;

const MobileFilterButton = styled.button`
  display: none;
  @media (max-width: 900px) {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 14px;
    border-radius: 10px;
    background: rgba(255, 255, 255, 0.03);
    border: 1px solid rgba(255, 255, 255, 0.04);
    color: #fff;
    cursor: pointer;
    margin-bottom: 12px;
  }
`;

const MobileOverlay = styled.div`
  position: fixed;
  top: 0;
  left: ${({ open }) => (open ? "0" : "-110%")};
  width: 78%;
  max-width: 360px;
  height: 100%;
  background: #111;
  box-shadow: 8px 0 30px rgba(0, 0, 0, 0.6);
  transition: left 0.28s ease;
  z-index: 2000;
  padding: 18px;
  overflow-y: auto;
`;

const Content = styled.main`
  flex: 1;
  display: flex;
  flex-direction: column;
  max-width: 1100px;
  margin: 0 auto;
  gap: 16px;
`;

const SearchBox = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(255, 255, 255, 0.02);
  border-radius: 10px;
  padding: 8px 12px;
  border: 1px solid rgba(255, 255, 255, 0.03);

  input {
    flex: 1;
    border: none;
    outline: none;
    background: transparent;
    color: #eee;
    font-size: 14px;
  }
`;

const IssuesGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 300px;
  gap: 20px;

  @media (max-width: 1000px) {
    grid-template-columns: 1fr;
  }
`;

const IssuesList = styled.div`
  display: grid;
  grid-auto-rows: min-content;
  gap: 18px;
`;

/* Fixed card width + height on desktop; responsive on mobile */
const IssueCard = styled.article`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(155, 92, 255, 0.08);
  border-radius: 12px;
  padding: 18px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  cursor: pointer;
  min-height: 320px; /* fixed height to keep uniformity */
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.5);
  @media (max-width: 480px) {
    right: 15px;
    bottom: 15px;
    font-size: 13px;
  }
  &:hover {
    transform: translateY(-6px);
    border-color: #c59fff;
    box-shadow: 0 12px 30px rgba(155, 92, 255, 0.12);
  }

  h3 {
    margin: 0;
    color: #fff;
    font-size: 1.05rem;
    line-height: 1.3;
    font-weight: 700;
  }

  p {
    margin: 0;
    color: #cfcfd6;
    font-size: 0.95rem;
    line-height: 1.4;
    max-height: 3.6rem;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const CardTop = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
`;

const CardHeader = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const AvatarWrapper = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  overflow: hidden;
  background: linear-gradient(135deg, #9b5cff, #c59fff);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const AvatarImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;

  span:first-child {
    font-weight: 700;
    color: #fff;
  }

  span:last-child {
    font-size: 12px;
    color: #aab1c7;
  }
`;

const Screenshot = styled.img`
  width: 100%;
  max-height: 140px;
  object-fit: cover;
  border-radius: 8px;
  margin-top: 6px;
`;

const CodeBlock = styled.pre`
  background: #0e0e0f;
  padding: 10px;
  border-radius: 8px;
  color: #e6e6e6;
  font-size: 13px;
  max-height: 120px;
  overflow: auto;
`;

const Meta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: auto; /* push meta+actions to bottom to keep card sizes */
`;

const Tag = styled.span`
  padding: 6px 10px;
  border-radius: 999px;
  font-weight: 700;
  font-size: 12px;
  color: #fff;
  background: ${({ variant }) =>
    variant === "type"
      ? "linear-gradient(90deg,#6c5ce7,#9b5cff)"
      : variant === "tech"
      ? "linear-gradient(90deg,#00cec9,#00fcc9)"
      : variant === "difficult"
      ? "linear-gradient(90deg,#ffb86b,#ff7a7a)"
      : "rgba(255,255,255,0.04)"};
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 8px;
  flex-wrap: wrap;

  button {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 8px 10px;
    border-radius: 8px;
    border: none;
    background: rgba(255, 255, 255, 0.02);
    color: #d6d6df;
    cursor: pointer;
    font-weight: 600;
    transition: background 0.18s ease, transform 0.12s ease;
  }

  button.primary {
    background: linear-gradient(90deg, #9b5cff, #c59fff);
    color: white;
  }

  button:active {
    transform: translateY(1px);
  }
`;

/* Right column (ads / features) */
export const AdsSection = styled.aside`
  background: rgba(255, 255, 255, 0.02);
  padding: 16px;
  border-radius: 12px;
  color: #fff;
  height: fit-content;
  border: 1px solid rgba(155, 92, 255, 0.06);

  h4 {
    color: #c59fff;
    margin-bottom: 12px;
  }
`;

const FeatureCard = styled.div`
  background: rgba(255, 255, 255, 0.02);
  padding: 12px;
  border-radius: 8px;
  margin-bottom: 10px;
  cursor: pointer;
  transition: transform 0.12s ease;
  border: 1px solid rgba(255, 255, 255, 0.02);

  h5 {
    margin: 0;
    color: #fff;
    display: flex;
    gap: 8px;
    align-items: center;
    font-size: 0.95rem;
  }

  p {
    margin: 6px 0 0;
    color: #cfcfd6;
    font-size: 0.9rem;
  }

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(155, 92, 255, 0.06);
  }
`;

/* Floating CTA button */
const FloatingButton = styled.button`
  position: fixed;
  bottom: 26px;
  right: 26px;
  background: linear-gradient(90deg, #9b5cff, #c59fff);
  color: white;
  padding: 12px 16px;
  border-radius: 999px;
  display: flex;
  gap: 10px;
  align-items: center;
  border: none;
  font-weight: 700;
  box-shadow: 0 8px 30px rgba(155, 92, 255, 0.2);
  cursor: pointer;
  z-index: 2000;

  &:hover {
    transform: translateY(-3px);
  }
`;

/* Modal styles reused from your original with polish */
export const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const ModalContent = styled.div`
  background: #111;
  color: #fff;
  width: 92%;
  max-width: 760px;
  border-radius: 12px;
  padding: 20px;
  max-height: 88vh;
  overflow-y: auto;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.6);
`;

const AIModal = styled.div`
  background: #0d0d0e;
  color: #e6e6e6;
  border-radius: 10px;
  padding: 16px;
  box-shadow: 0 8px 40px rgba(155, 92, 255, 0.12);
`;

/* small helper components */
const Small = styled.span`
  font-size: 12px;
  color: #9aa0b3;
`;

/* --------------------------------------------------------------------------
   Component logic (all original features kept + UI fixes)
   -------------------------------------------------------------------------- */

const PAGE_SIZE = 20;

const IssuesListPage = () => {
  // filters & UI state
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  const [technology, setTechnology] = useState("");
  const [type, setType] = useState("");
  const [difficulty, setDifficulty] = useState("");
  const [postedTime, setPostedTime] = useState("");
  const [reward, setReward] = useState([0, 1000000]);
  const [tempReward, setTempReward] = useState(reward);
  const [showFullCode, setShowFullCode] = useState(null);
  const [openSuggest, setOpenSuggest] = useState(null);
  const [suggestText, setSuggestText] = useState("");
  const [addingSuggestion, setAddingSuggestion] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);

  // data & pagination
  const [issues, setIssues] = useState([]);
  const [lastDoc, setLastDoc] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  // likes and user-likes local state
  const [likes, setLikes] = useState({});
  const [userLikes, setUserLikes] = useState({});

  // AI modal
  const [aiModalOpen, setAiModalOpen] = useState(false);
  const [activeAISolution, setActiveAISolution] = useState(null);
  const [activeIssue, setActiveIssue] = useState(null);
  const [loadingAISolution, setLoadingAISolution] = useState(false);

  // delete / solved modals
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [showSolvedModal, setShowSolvedModal] = useState(false);
  const [solvedId, setSolvedId] = useState(null);

  // auth modal
  const [showAuthModal, setShowAuthModal] = useState(false);

  const { user } = useContext(UserContext) || {};
  const navigate = useNavigate();

  const observer = useRef();
  useSEO({
    title: "Explore Code Issues | Hire Developers & Fix Bugs Fast",
    description:
      "Browse real coding issues posted by developers. Offer solutions, earn money, and grow your rank on SolveMyCodeIssue.com — the dev community for instant problem-solving.",
    keywords:
      "code issues, solve coding problems, hire developers, fix bugs online, programming help, coding community, post code issue, earn by solving code",
    canonical: "https://solvemycodeissue.com/code-issues",
    ogImage: "https://solvemycodeissue.com/og-image-code-issues.png",
  });
  // technologies list (kept inline as before)
  const technologies = [
    "React",
    "React Native",
    "Node.js",
    "Firebase",
    "Next.js",
    "Angular",
    "Vue.js",
    "Express.js",
    "MongoDB",
    "PostgreSQL",
    "MySQL",
    "GraphQL",
    "TypeScript",
    "JavaScript",
    "HTML",
    "CSS",
    "Tailwind CSS",
    "Bootstrap",
    "Python",
    "Django",
    "Flask",
    "Java",
    "Spring Boot",
    "Kotlin",
    "Swift",
    "C#",
    ".NET",
    "PHP",
    "Laravel",
    "Ruby on Rails",
    "Docker",
    "Kubernetes",
    "AWS",
    "Google Cloud",
    "Azure",
  ];

  /* ------------------- Fetching issues (infinite scroll friendly) ------------------- */
  const fetchIssues = async (next = false) => {
    if (loading) return;
    if (next && !hasMore) return;
    setLoading(true);
    try {
      let q = query(
        collection(db, "codeIssues"),
        orderBy("createdAt", "desc"),
        limit(PAGE_SIZE)
      );

      if (next && lastDoc) {
        q = query(
          collection(db, "codeIssues"),
          orderBy("createdAt", "desc"),
          startAfter(lastDoc),
          limit(PAGE_SIZE)
        );
      }

      const snap = await getDocs(q);
      const docs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

      if (next) {
        setIssues((prev) => [...prev, ...docs]);
      } else {
        setIssues(docs);
      }

      setLastDoc(snap.docs[snap.docs.length - 1] || null);
      setHasMore(snap.docs.length === PAGE_SIZE);
    } catch (err) {
      console.error("Error fetching issues:", err);
    } finally {
      setLoading(false);
    }
  };

  // initial fetch and when filters change
  useEffect(() => {
    // Note: original code did client-side filter after fetching; keep behavior
    fetchIssues(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [technology, type, difficulty, reward, postedTime]);

  /* ------------------- Likes: fetch and toggle ------------------- */

  const fetchLikes = async (issueId) => {
    try {
      const likesRef = collection(db, "codeIssues", issueId, "likes");
      const snap = await getDocs(likesRef);
      setLikes((prev) => ({ ...prev, [issueId]: snap.size }));

      if (user?.id) {
        const q = query(likesRef, where("userId", "==", user.id));
        const likedSnap = await getDocs(q);
        setUserLikes((prev) => ({ ...prev, [issueId]: !likedSnap.empty }));
      }
    } catch (err) {
      console.error("Error fetching likes for", issueId, err);
    }
  };

  useEffect(() => {
    // fetch likes for displayed issues
    issues.forEach((issue) => {
      fetchLikes(issue.id);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [issues, user?.id]);

  const updateIssueInState = (id, updates) => {
    setIssues((prev) =>
      prev.map((it) => (it.id === id ? { ...it, ...updates } : it))
    );
  };

  const toggleLike = async (issueId) => {
    if (!user?.id) {
      setShowAuthModal(true);
      return;
    }

    try {
      const likesRefColl = collection(db, "codeIssues", issueId, "likes");
      const issueRef = doc(db, "codeIssues", issueId);

      if (userLikes[issueId]) {
        // find and delete user's like doc
        const q = query(likesRefColl, where("userId", "==", user.id));
        const snap = await getDocs(q);
        snap.forEach(async (d) => {
          await deleteDoc(doc(db, "codeIssues", issueId, "likes", d.id));
        });

        setUserLikes((prev) => ({ ...prev, [issueId]: false }));
        setLikes((prev) => ({ ...prev, [issueId]: (prev[issueId] || 1) - 1 }));
        await updateDoc(issueRef, { likesCount: increment(-1) });
        updateIssueInState(issueId, { likesCount: (likes[issueId] || 1) - 1 });
      } else {
        await addDoc(likesRefColl, {
          userId: user.id,
          createdAt: serverTimestamp(),
        });
        setUserLikes((prev) => ({ ...prev, [issueId]: true }));
        setLikes((prev) => ({ ...prev, [issueId]: (prev[issueId] || 0) + 1 }));
        await updateDoc(issueRef, { likesCount: increment(1) });
        updateIssueInState(issueId, { likesCount: (likes[issueId] || 0) + 1 });
      }
    } catch (err) {
      console.error("Error toggling like:", err);
    }
  };

  /* ------------------- Suggestions ------------------- */
  const handleSuggestSubmit = async (issueId) => {
    if (!suggestText.trim()) return;
    if (!user?.id) {
      setShowAuthModal(true);
      return;
    }

    try {
      setAddingSuggestion(true);
      const suggestRef = collection(db, "codeIssues", issueId, "suggestions");
      await addDoc(suggestRef, {
        text: suggestText.trim(),
        userId: user.id,
        userName: user.name || "Anonymous",
        userImage: user.profilePic || "",
        createdAt: serverTimestamp(),
      });

      const issueRef = doc(db, "codeIssues", issueId);
      await updateDoc(issueRef, { suggestionCount: increment(1) });

      updateIssueInState(issueId, {
        suggestionCount:
          (issues.find((i) => i.id === issueId)?.suggestionCount || 0) + 1,
      });

      setSuggestText("");
      setOpenSuggest(null);
    } catch (err) {
      console.error("Error adding suggestion:", err);
    } finally {
      setAddingSuggestion(false);
    }
  };

  /* ------------------- AI Solution retrieval & modal ------------------- */
  const openAISolutionModal = async (issue) => {
    try {
      setActiveIssue(issue);
      setAiModalOpen(true);
      setLoadingAISolution(true);

      const solRef = collection(db, "codeIssues", issue.id, "solutions");
      const solSnap = await getDocs(solRef);
      const aiDoc = solSnap.docs.find((d) => d.data().type === "ai");

      if (aiDoc) {
        setActiveAISolution(aiDoc.data().content);
      } else {
        setActiveAISolution("No AI solution found for this issue yet.");
      }
    } catch (err) {
      console.error("Error fetching AI solution:", err);
      setActiveAISolution("Failed to fetch AI solution.");
    } finally {
      setLoadingAISolution(false);
    }
  };

  /* ------------------- Edit / Delete / Mark Solved ------------------- */
  const handleEdit = (id) => {
    navigate(`/edit-code-issue/${id}`);
  };

  const handleDelete = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    setShowDeleteModal(false);
    if (!deleteId) return;
    try {
      await deleteDoc(doc(db, "codeIssues", deleteId));
      setIssues((prev) => prev.filter((i) => i.id !== deleteId));
    } catch (err) {
      console.error("Error deleting issue:", err);
    } finally {
      setDeleteId(null);
    }
  };

  const handleMarkSolved = (id) => {
    setSolvedId(id);
    setShowSolvedModal(true);
  };

  const confirmSolved = async () => {
    setShowSolvedModal(false);
    if (!solvedId) return;
    try {
      const issueRef = doc(db, "codeIssues", solvedId);
      await updateDoc(issueRef, {
        status: "SOLVED",
        solvedAt: serverTimestamp(),
      });
      setIssues((prev) =>
        prev.map((i) => (i.id === solvedId ? { ...i, status: "SOLVED" } : i))
      );
    } catch (err) {
      console.error("Error marking solved:", err);
    } finally {
      setSolvedId(null);
    }
  };

  /* ------------------- Chat room (per-issue) ------------------- */
  const ensureChatRoomAndGo = async (issue) => {
    if (!user?.id) {
      setShowAuthModal(true);
      return;
    }

    try {
      const roomRef = doc(db, "chatRooms", issue.id);
      const roomSnap = await getDoc(roomRef);

      if (!roomSnap.exists()) {
        await setDoc(roomRef, {
          issueId: issue.id,
          issueTitle: issue.title || "Issue",
          createdBy: issue.postedBy,
          participants: [issue.posterId, user.id].filter(Boolean),
          createdAt: serverTimestamp(),
          lastMessageAt: serverTimestamp(),
        });
      } else {
        await updateDoc(roomRef, {
          participants: arrayUnion(user.id),
          lastMessageAt: serverTimestamp(),
        });
      }

      navigate(`/chats`);
    } catch (err) {
      console.error("Failed to open chat room:", err);
      alert("Unable to open chat right now.");
    }
  };

  /* ------------------- Infinite scroll observer ------------------- */
  const lastIssueRef = useCallback(
    (node) => {
      if (loading) return;
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          // load next page
          fetchIssues(true);
        }
      });

      if (node) observer.current.observe(node);
    },
    [loading, hasMore] // dependencies
  );

  /* --------------------------------------------------------------------------
     Render
     -------------------------------------------------------------------------- */

  return (
    <Container>
      {/* Sidebar (desktop) */}
      <Sidebar>
        <h3>Filters</h3>

        <div style={{ marginBottom: 12 }}>
          <Small>Technology</Small>
          <select
            value={technology}
            onChange={(e) => setTechnology(e.target.value)}
            style={{
              width: "100%",
              marginTop: 8,
              padding: "10px",
              background: "transparent",
              color: "#fff",
              borderRadius: 8,
              border: "1px solid rgba(255,255,255,0.04)",
            }}
          >
            <option value="">All technologies</option>
            {technologies.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </div>

        <div style={{ marginBottom: 12 }}>
          <Small>Type</Small>
          <select
            value={type}
            onChange={(e) => setType(e.target.value)}
            style={{
              width: "100%",
              marginTop: 8,
              padding: "10px",
              background: "transparent",
              color: "#fff",
              borderRadius: 8,
              border: "1px solid rgba(255,255,255,0.04)",
            }}
          >
            <option value="">All types</option>
            <option value="free">Free</option>
            <option value="paid">Paid</option>
          </select>
        </div>

        <div style={{ marginBottom: 12 }}>
          <Small>Difficulty</Small>
          <select
            value={difficulty}
            onChange={(e) => setDifficulty(e.target.value)}
            style={{
              width: "100%",
              marginTop: 8,
              padding: "10px",
              background: "transparent",
              color: "#fff",
              borderRadius: 8,
              border: "1px solid rgba(255,255,255,0.04)",
            }}
          >
            <option value="">All</option>
            <option value="easy">Easy</option>
            <option value="medium">Medium</option>
            <option value="hard">Hard</option>
          </select>
        </div>

        <div style={{ marginBottom: 12 }}>
          <Small>Reward Range</Small>
          <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
            <input
              type="number"
              value={reward[0]}
              onChange={(e) =>
                setReward([Number(e.target.value || 0), reward[1]])
              }
              style={{
                width: "100px",
                padding: 10,
                borderRadius: 8,
                border: "1px solid rgba(255,255,255,0.04)",
                background: "transparent",
                color: "#fff",
              }}
            />
            <input
              type="number"
              value={reward[1]}
              onChange={(e) =>
                setReward([reward[0], Number(e.target.value || 0)])
              }
              style={{
                width: "100px",
                padding: 10,
                borderRadius: 8,
                border: "1px solid rgba(255,255,255,0.04)",
                background: "transparent",
                color: "#fff",
              }}
            />
          </div>
          <Small style={{ marginTop: 8 }}>
            {reward[0]} — {reward[1]}
          </Small>
        </div>

        <div style={{ marginTop: 12 }}>
          <Small>Posted Time</Small>
          <select
            value={postedTime}
            onChange={(e) => setPostedTime(e.target.value)}
            style={{
              width: "100%",
              marginTop: 8,
              padding: "10px",
              background: "transparent",
              color: "#fff",
              borderRadius: 8,
              border: "1px solid rgba(255,255,255,0.04)",
            }}
          >
            <option value="">Any time</option>
            <option value="24h">Last 24 hours</option>
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
          </select>
        </div>
      </Sidebar>

      {/* Mobile overlay for filters */}
      <MobileOverlay open={mobileFilterOpen}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 12,
          }}
        >
          <h3 style={{ color: "#c59fff" }}>Filters</h3>
          <button
            onClick={() => setMobileFilterOpen(false)}
            style={{ background: "transparent", border: "none", color: "#fff" }}
          >
            <X />
          </button>
        </div>

        {/* replicate filter controls for mobile */}
        <div style={{ marginBottom: 12 }}>
          <Small>Type</Small>
          <select
            value={type}
            onChange={(e) => setType(e.target.value)}
            style={{ width: "100%", marginTop: 8, padding: 10 }}
          >
            <option value="">All</option>
            <option value="free">Free</option>
            <option value="paid">Paid</option>
          </select>
        </div>

        <div style={{ marginBottom: 12 }}>
          <Small>Difficulty</Small>
          <select
            value={difficulty}
            onChange={(e) => setDifficulty(e.target.value)}
            style={{ width: "100%", marginTop: 8, padding: 10 }}
          >
            <option value="">All</option>
            <option value="easy">Easy</option>
            <option value="medium">Medium</option>
            <option value="hard">Hard</option>
          </select>
        </div>

        <div style={{ marginBottom: 12 }}>
          <Small>Reward Range</Small>
          <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
            <input
              type="number"
              value={reward[0]}
              onChange={(e) =>
                setReward([Number(e.target.value || 0), reward[1]])
              }
              style={{ flex: 1, padding: 10 }}
            />
            <input
              type="number"
              value={reward[1]}
              onChange={(e) =>
                setReward([reward[0], Number(e.target.value || 0)])
              }
              style={{ flex: 1, padding: 10 }}
            />
          </div>
        </div>
      </MobileOverlay>

      <Content>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: 12,
            alignItems: "center",
          }}
        >
          <div style={{ display: "flex", gap: 12, width: "100%" }}>
            <SearchBox style={{ flex: 1 }}>
              <Search size={18} color="#bbb" />
              <input placeholder="Search issues, tags, titles (todo: wire up search)" />
            </SearchBox>

            <MobileFilterButton onClick={() => setMobileFilterOpen(true)}>
              <Filter /> Filters
            </MobileFilterButton>
          </div>
        </div>

        <IssuesGrid>
          <IssuesList>
            {loading && (
              <div style={{ textAlign: "center", padding: 20 }}>
                <Loader2 size={22} className="animate-spin" />
              </div>
            )}

            {issues.length === 0 && !loading && (
              <div style={{ textAlign: "center", color: "#9aa0b3" }}>
                No issues found.
              </div>
            )}

            {issues.map((issue, idx) => {
              const isLast = idx === issues.length - 1;
              const showFull = showFullCode === issue.id;
              return (
                <IssueCard
                  key={issue.id}
                  ref={isLast ? lastIssueRef : null}
                  onClick={(e) => {
                    // don't navigate if clicking buttons inside card
                    if (
                      e.target.closest("button") ||
                      e.target.closest(".menu-button")
                    )
                      return;
                    navigate(`/code-issue/${issue.id}`);
                  }}
                >
                  <CardTop>
                    <CardHeader>
                      <AvatarWrapper>
                        {issue.userImage ? (
                          <AvatarImage
                            src={issue.userImage}
                            alt={issue.postedBy}
                          />
                        ) : (
                          <User color="#fff" />
                        )}
                      </AvatarWrapper>

                      <UserInfo>
                        <span>{issue.postedBy || "Anonymous"}</span>
                        {issue.createdAt?.seconds ? (
                          <span>
                            {new Date(
                              issue.createdAt.seconds * 1000
                            ).toLocaleDateString()}{" "}
                            •{" "}
                            {new Date(
                              issue.createdAt.seconds * 1000
                            ).toLocaleTimeString()}
                          </span>
                        ) : (
                          <Small>Just now</Small>
                        )}
                      </UserInfo>
                    </CardHeader>

                    {/* menu for poster */}
                    {user?.id === issue.posterId && (
                      <div
                        style={{ position: "relative" }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button
                          className="menu-button"
                          style={{
                            background: "transparent",
                            border: "none",
                            color: "#cfcfe6",
                            padding: 6,
                            borderRadius: 8,
                            cursor: "pointer",
                          }}
                          onClick={(ev) => {
                            ev.stopPropagation();
                            setOpenMenu(
                              openMenu === issue.id ? null : issue.id
                            );
                          }}
                        >
                          <MoreVertical color="white" />
                        </button>

                        {openMenu === issue.id && (
                          <div
                            style={{
                              position: "absolute",
                              right: 0,
                              top: 36,
                              background: "#111",
                              border: "1px solid rgba(255,255,255,0.04)",
                              borderRadius: 8,
                              overflow: "hidden",
                              zIndex: 50,
                            }}
                            onClick={(ev) => ev.stopPropagation()}
                          >
                            <button
                              onClick={() => handleEdit(issue.id)}
                              style={{
                                display: "flex",
                                gap: 8,
                                padding: 10,
                                background: "transparent",
                                border: "none",
                                width: "100%",
                                color: "#fff",
                                alignItems: "center",
                              }}
                            >
                              <Edit size={16} /> Edit
                            </button>
                            <button
                              onClick={() => handleDelete(issue.id)}
                              style={{
                                display: "flex",
                                gap: 8,
                                padding: 10,
                                background: "transparent",
                                border: "none",
                                width: "100%",
                                color: "#ff7a7a",
                                alignItems: "center",
                              }}
                            >
                              <Trash2 size={16} /> Delete
                            </button>
                            {issue.status === "UNSOLVED" && (
                              <button
                                onClick={() => handleMarkSolved(issue.id)}
                                style={{
                                  display: "flex",
                                  gap: 8,
                                  padding: 10,
                                  background: "transparent",
                                  border: "none",
                                  width: "100%",
                                  color: "#aaffc9",
                                  alignItems: "center",
                                }}
                              >
                                <CheckCircle2 size={16} /> Mark as Solved
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </CardTop>

                  <div>
                    <h3>{issue.title}</h3>
                    <p>{issue.description}</p>

                    {issue.screenshot && (
                      <Screenshot src={issue.screenshot} alt="screenshot" />
                    )}

                    {issue.code && (
                      <>
                        <CodeBlock>
                          {showFull
                            ? issue.code
                            : issue.code.split("\n").slice(0, 6).join("\n") +
                              (issue.code.split("\n").length > 6
                                ? "\n..."
                                : "")}
                        </CodeBlock>
                        {issue.code.split("\n").length > 6 && (
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "flex-end",
                              marginTop: 6,
                            }}
                          >
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setShowFullCode(showFull ? null : issue.id);
                              }}
                              style={{
                                background: "transparent",
                                border: "none",
                                color: "#c59fff",
                                fontWeight: 700,
                                cursor: "pointer",
                              }}
                            >
                              {showFull ? "Hide code" : "View full code"}
                            </button>
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  <Meta>
                    <Tag variant="type">{issue.type || "Free"}</Tag>
                    <Tag variant="tech">{issue.technology || "Unknown"}</Tag>
                    {issue.difficulty && (
                      <Tag variant="difficult">{issue.difficulty}</Tag>
                    )}
                    <Tag variant="status">
                      {(issue.status || "UNSOLVED").toLowerCase()}
                    </Tag>
                    <Small style={{ marginLeft: "auto", alignSelf: "center" }}>
                      {issue.reward ? `${issue.reward} coins` : ""}
                    </Small>
                  </Meta>

                  <Actions onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={() => {
                        if (!user?.id) return setShowAuthModal(true);
                        toggleLike(issue.id);
                      }}
                    >
                      <ThumbsUp
                        size={16}
                        color={userLikes[issue.id] ? "#60A5FA" : undefined}
                      />
                      {likes[issue.id] || issue.likesCount || 0}
                    </button>

                    <button
                      onClick={() => {
                        if (!user?.id) {
                          setShowAuthModal(true);
                          return;
                        }
                        setOpenSuggest(
                          openSuggest === issue.id ? null : issue.id
                        );
                        setSuggestText("");
                      }}
                    >
                      <Lightbulb size={16} /> {issue.suggestionCount || 0}
                    </button>

                    <button onClick={() => openAISolutionModal(issue)}>
                      <Cpu size={16} color="#c59fff" /> AI Solution
                    </button>

                    <button
                      onClick={() => {
                        ensureChatRoomAndGo(issue);
                      }}
                    >
                      <MessageSquare size={16} /> Chat
                    </button>
                  </Actions>

                  {/* Suggestion box */}
                  {openSuggest === issue.id && (
                    <div
                      style={{ marginTop: 10 }}
                      onClick={(ev) => ev.stopPropagation()}
                    >
                      <div
                        style={{
                          display: "flex",
                          gap: 8,
                          alignItems: "flex-start",
                        }}
                      >
                        <AvatarWrapper style={{ width: 40, height: 40 }}>
                          {user?.profilePic ? (
                            <AvatarImage src={user.profilePic} />
                          ) : (
                            <User color="#fff" />
                          )}
                        </AvatarWrapper>
                        <div style={{ flex: 1 }}>
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <div style={{ fontWeight: 700 }}>
                              {user?.name || "You"}
                            </div>
                          </div>

                          <textarea
                            value={suggestText}
                            onChange={(e) => setSuggestText(e.target.value)}
                            placeholder="Write your suggestion..."
                            style={{
                              width: "100%",
                              marginTop: 8,
                              padding: 10,
                              borderRadius: 8,
                              background: "#0f0f10",
                              color: "#fff",
                              border: "1px solid rgba(255,255,255,0.04)",
                              minHeight: 70,
                            }}
                          />

                          <div
                            style={{
                              display: "flex",
                              justifyContent: "flex-end",
                              gap: 8,
                              marginTop: 8,
                            }}
                          >
                            <button
                              onClick={() => {
                                setOpenSuggest(null);
                                setSuggestText("");
                              }}
                              style={{
                                background: "transparent",
                                border: "none",
                                color: "#9aa0b3",
                                cursor: "pointer",
                              }}
                            >
                              Cancel
                            </button>
                            <button
                              onClick={() => handleSuggestSubmit(issue.id)}
                              disabled={!suggestText.trim() || addingSuggestion}
                              style={{
                                background:
                                  "linear-gradient(90deg,#9b5cff,#c59fff)",
                                color: "#fff",
                                border: "none",
                                padding: "8px 12px",
                                borderRadius: 8,
                                cursor: "pointer",
                                fontWeight: 700,
                              }}
                            >
                              {addingSuggestion ? "Submitting..." : "Submit"}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </IssueCard>
              );
            })}
          </IssuesList>

          {/* Right column features / ads */}
          <AdsSection>
            <h4>Explore Features</h4>

            <FeatureCard
              onClick={() => {
                navigate("/post-referral-job");
              }}
            >
              <h5>
                <PlusCircle size={16} /> Post Referral Jobs
              </h5>
              <p>Share referral jobs and help your network grow.</p>
            </FeatureCard>

            <FeatureCard
              onClick={() => {
                navigate("/members");
              }}
            >
              <h5>
                <PlusCircle size={16} /> Connect With Members
              </h5>
              <p>Find and chat with other developers instantly.</p>
            </FeatureCard>
          </AdsSection>
        </IssuesGrid>
      </Content>

      {/* Floating CTA */}
      <FloatingButton
        onClick={() => {
          if (user) navigate("/post-code-issue");
          else setShowAuthModal(true);
        }}
      >
        <Plus size={16} />
        Add Issue
      </FloatingButton>

      {/* Auth modal */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
      />

      {/* Delete confirm modal */}
      {showDeleteModal && (
        <ModalOverlay>
          <ModalContent>
            <h3 style={{ color: "#c59fff" }}>Confirm Delete</h3>
            <p>
              Are you sure you want to delete this issue? This action cannot be
              undone.
            </p>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 10,
                marginTop: 16,
              }}
            >
              <button
                onClick={() => setShowDeleteModal(false)}
                style={{
                  background: "#222",
                  color: "#fff",
                  padding: "8px 12px",
                  borderRadius: 8,
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                style={{
                  background: "#ef4444",
                  color: "#fff",
                  padding: "8px 12px",
                  borderRadius: 8,
                }}
              >
                Delete
              </button>
            </div>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* Solved confirm modal */}
      {showSolvedModal && (
        <ModalOverlay>
          <ModalContent>
            <h3 style={{ color: "#c59fff" }}>Mark as Solved</h3>
            <p>
              Mark this issue as solved? This will update its status for all
              users.
            </p>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 10,
                marginTop: 16,
              }}
            >
              <button
                onClick={() => setShowSolvedModal(false)}
                style={{
                  background: "#222",
                  color: "#fff",
                  padding: "8px 12px",
                  borderRadius: 8,
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmSolved}
                style={{
                  background: "#10b981",
                  color: "#fff",
                  padding: "8px 12px",
                  borderRadius: 8,
                }}
              >
                Confirm
              </button>
            </div>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* AI solution modal */}
      {aiModalOpen && (
        <ModalOverlay onClick={() => setAiModalOpen(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: 12,
                alignItems: "center",
              }}
            >
              <div>
                <h2 style={{ margin: 0, color: "#c59fff" }}>🤖 AI Solution</h2>
                <Small style={{ marginTop: 6 }}>{activeIssue?.title}</Small>
              </div>
              <button
                onClick={() => setAiModalOpen(false)}
                style={{
                  background: "transparent",
                  border: "none",
                  color: "#cfcfe6",
                  cursor: "pointer",
                }}
              >
                <X />
              </button>
            </div>

            <div style={{ marginTop: 12 }}>
              {loadingAISolution ? (
                <div style={{ textAlign: "center", padding: 20 }}>
                  <Loader2 className="animate-spin" />
                  <div style={{ color: "#c59fff", marginTop: 8 }}>
                    Loading AI solution...
                  </div>
                </div>
              ) : (
                <AIModal>
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                      code({ node, inline, className, children, ...props }) {
                        const match = /language-(\w+)/.exec(className || "");
                        return !inline && match ? (
                          <SyntaxHighlighter
                            style={oneDark}
                            language={match[1]}
                            PreTag="div"
                            {...props}
                          >
                            {String(children).replace(/\n$/, "")}
                          </SyntaxHighlighter>
                        ) : (
                          <code
                            style={{
                              background: "#111",
                              padding: "0.2rem 0.4rem",
                              borderRadius: 4,
                              color: "#c59fff",
                            }}
                            {...props}
                          >
                            {children}
                          </code>
                        );
                      },
                      h1: ({ node, ...props }) => (
                        <h1 style={{ color: "#c59fff" }} {...props} />
                      ),
                      h2: ({ node, ...props }) => (
                        <h2 style={{ color: "#c59fff" }} {...props} />
                      ),
                      p: ({ node, ...props }) => (
                        <p style={{ color: "#d6d6df" }} {...props} />
                      ),
                      li: ({ node, ...props }) => (
                        <li style={{ color: "#d6d6df" }} {...props} />
                      ),
                    }}
                  >
                    {activeAISolution || "No AI solution available."}
                  </ReactMarkdown>
                </AIModal>
              )}
            </div>
          </ModalContent>
        </ModalOverlay>
      )}
    </Container>
  );
};

export default IssuesListPage;
